// ── Adapter Supabase do IAgendaRepository ─────────────────────────
// Implementa a porta do módulo Agenda usando o cliente Supabase. É a ÚNICA
// camada que conhece tabelas/RPCs do banco e dispara os webhooks (n8n/MKT)
// interligados às operações de dados. O núcleo (agendaService) não toca aqui.

import { getSupabase, type TypedSupabaseClient } from '@infra/supabase/client';
import type { MktAction, NotifyMktConversionOpts } from '@modules/mkt';
import type { DispatchWebhookParams } from '@modules/webhooks';
import { generateMeetLink } from '@modules/agenda';
import type { Database, Json } from '@/types/database.types';
import type { AgendaMeeting, WonCard } from '@/types/agenda';
import type {
  IAgendaRepository,
  GetMeetingsByWeekParams,
  GetAvailableSlotsParams,
  AvailableSlot,
  ScheduleMeetingParams,
  UpdateMeetingLinkParams,
  EditMeetingScheduleParams,
  SwapMeetingCloserParams,
  SwapMeetingCloserResult,
  DiscardRescheduleParams,
  HandoffLeadToAgendaParams,
  PendingAgendaMeetingsParams,
  PendingReschedulesParams,
  PendingRescheduleRow,
  ConfirmPresenceParams,
  CancelMeetingParams,
  RegisterNoShowParams,
  RegisterResultParams,
  CloserScheduleRow,
  CloserScheduleInsert,
  BlockedDateRow,
  BlockedDateInsert,
  SchedulePermRow,
  SchedulePermWithEditor,
  AddSchedulePermParams,
  ConfigKV,
  TimeMemberRow,
  WorkspaceMemberRow,
  PipelineMemberRow,
  MeetingOrdinalRow,
  SdrCardForLead,
  CloserPipeline,
  UpdateMeetingStatusDirectParams,
  FindCloserPipelineParams,
  PipelineRow,
} from '@modules/agenda';

// ──────────────────────────────────────────────────────────────────
//  Aliases de tipos do banco
// ──────────────────────────────────────────────────────────────────
type DbMeetingStatus = Database['public']['Enums']['meeting_status'];
type MeetingsUpdate = Database['public']['Tables']['meetings']['Update'];
type CardsUpdate = Database['public']['Tables']['cards']['Update'];
type CardsInsert = Database['public']['Tables']['cards']['Insert'];
type HistoryInsert = Database['public']['Tables']['history']['Insert'];

// Linha "leve" de meeting usada nos webhooks (link + closer embed).
interface MeetCloserLite {
  link: string | null;
  closer_id: string | null;
  scheduled_at?: string | null;
  duration_minutes?: number | null;
  card_id?: string | null;
  closer: { nome: string | null } | null;
}

// Webhook n8n de cancelamento do evento/link (Google Meet).
const N8N_CANCEL_WEBHOOK = 'https://n8n.2.empreenderdinheiro.com.br/webhook/crm/cancelar-reunião';

interface PostCancelMeetingWebhookParams {
  closerNome: string | null;
  closerId: string | null;
  link: string | null;
}

const postCancelMeetingWebhook = ({ closerNome, closerId, link }: PostCancelMeetingWebhookParams): void => {
  if (!link) return;
  const n8nUser = process.env.REACT_APP_N8N_MEET_WEBHOOK_USER;
  const n8nPass = process.env.REACT_APP_N8N_MEET_WEBHOOK_PASS;
  const auth = n8nUser && n8nPass && typeof btoa === 'function'
    ? btoa(`${n8nUser}:${n8nPass}`)
    : null;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (auth) headers.Authorization = `Basic ${auth}`;
  fetch(N8N_CANCEL_WEBHOOK, {
    method: 'POST',
    headers,
    body: JSON.stringify({ closer: closerNome, closer_id: closerId, link }),
  }).catch(err => console.warn('[SupabaseAgendaRepository] webhook cancelar falhou:', (err as Error)?.message));
};

// ── Parser dos eventos da agenda do Google (via webhook n8n) ────────
// O n8n devolve o evento OCUPADO como um objeto único no topo (formato nativo
// do Google Calendar: { start:{dateTime}, end:{dateTime}, ... }) com HTTP 200.
// Quando o closer está LIVRE, responde HTTP 500 { code:0, message:"No item to
// return was found" } — tratado como "sem eventos" na chamada. Ainda assim
// aceitamos array cru / { items|events|data } por robustez a mudanças no fluxo;
// cada evento pode expor start/end como string ISO ou objeto { dateTime|date }.
const normalizeGoogleEvents = (body: unknown): unknown[] => {
  if (!body || typeof body !== 'object') return [];
  if (Array.isArray(body)) return body;
  const rec = body as Record<string, unknown>;
  if (Array.isArray(rec.items))  return rec.items as unknown[];
  if (Array.isArray(rec.events)) return rec.events as unknown[];
  if (Array.isArray(rec.data))   return rec.data as unknown[];
  // Evento único no topo (shape observado no endpoint). O objeto de erro
  // { code, message } também cai aqui, mas é descartado por não ter start.
  return [body];
};

const pickEventDateTime = (ev: Record<string, unknown>, keys: string[]): string | null => {
  for (const key of keys) {
    const v = ev[key];
    if (!v) continue;
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      const obj = v as Record<string, unknown>;
      if (typeof obj.dateTime === 'string') return obj.dateTime;
      if (typeof obj.date === 'string') return obj.date;
    }
  }
  return null;
};

const parseGoogleBusyIntervals = (body: unknown): Array<{ start: string; end: string }> => {
  const arr = normalizeGoogleEvents(body);
  const out: Array<{ start: string; end: string }> = [];
  for (const raw of arr) {
    if (!raw || typeof raw !== 'object') continue;
    const ev = raw as Record<string, unknown>;
    const start = pickEventDateTime(ev, ['start', 'inicio', 'dataInicio', 'start_time', 'dataHoraInicio']);
    const end   = pickEventDateTime(ev, ['end', 'fim', 'dataFim', 'end_time', 'dataHoraFim']);
    if (start) out.push({ start, end: end || start });
  }
  return out;
};

const requireSupabase = (): TypedSupabaseClient => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase não inicializado.');
  return s;
};

const throwIfError = (error: unknown): void => {
  if (!error) return;
  const e = error as { message?: string; details?: string };
  throw new Error(e.message || e.details || JSON.stringify(error));
};

// Config/42: localiza o stage de devolução do SDR (cancelamento / no-show).
const findEmContatoStageId = async (supabase: TypedSupabaseClient, pipelineId: string | null): Promise<string | null> => {
  if (!pipelineId) return null;
  const { data: byRole } = await supabase
    .from('pipeline_stages')
    .select('id')
    .eq('pipeline_id', pipelineId)
    .eq('stage_role', 'em_contato')
    .eq('ativo', true)
    .maybeSingle();
  if (byRole?.id) return byRole.id;

  const { data: stg } = await supabase
    .from('pipeline_stages')
    .select('id, name')
    .eq('pipeline_id', pipelineId)
    .eq('ativo', true);
  const target = (stg || []).find(s => /em.contato/i.test(s.name || '')) || null;
  return target?.id || null;
};

// Localiza a etapa "Finalizado" do SDR ao registrar reunião como realizada.
const findFinalizadoStageId = async (supabase: TypedSupabaseClient, pipelineId: string | null): Promise<string | null> => {
  if (!pipelineId) return null;
  const { data: stages } = await supabase
    .from('pipeline_stages')
    .select('id, name, order_index')
    .eq('pipeline_id', pipelineId)
    .eq('ativo', true)
    .order('order_index', { ascending: true });
  if (!stages || stages.length === 0) return null;

  const byName = stages.find(s => /finaliz/i.test(s.name || ''));
  if (byName?.id) return byName.id;

  return stages[stages.length - 1]?.id || null;
};

const SELECT_RICH = `
  *,
  closer:profiles!meetings_closer_id_fkey ( id, nome, initials, color, avatar_url ),
  sdr:profiles!meetings_sdr_id_fkey       ( id, nome, initials, color, avatar_url ),
  creator:profiles!meetings_created_by_fkey ( id, nome, initials, color, avatar_url ),
  lead:leads (
    id, nome, telefone, patrimonio, origem, canal, status,
    lead_sdr:profiles!leads_sdr_id_fkey ( id, nome, initials, color, avatar_url ),
    lead_closer:profiles!leads_closer_id_fkey ( id, nome, initials, color, avatar_url )
  ),
  card:cards ( id, pipeline_id, stage_id, confirmed, reagend, status, ativo, linked_card_id )
`;

const enrichMeetingCards = async (supabase: TypedSupabaseClient, meetings: AgendaMeeting[]): Promise<AgendaMeeting[]> => {
  const rows = meetings || [];
  const cards = rows.map(row => row.card).filter((c): c is NonNullable<AgendaMeeting['card']> => Boolean(c));
  const linkedCardIds = [...new Set(cards.map(card => card.linked_card_id).filter(Boolean))];

  let linkedCards: Array<{ id: string; pipeline_id: string; status: string; ativo: boolean | null }> = [];
  if (linkedCardIds.length) {
    const { data, error } = await supabase
      .from('cards')
      .select('id, pipeline_id, status, ativo')
      .in('id', linkedCardIds as string[]);
    throwIfError(error);
    linkedCards = data || [];
  }

  const pipelineIds = [
    ...cards.map(card => card.pipeline_id),
    ...linkedCards.map(card => card.pipeline_id),
  ].filter(Boolean);
  const uniquePipelineIds = [...new Set(pipelineIds)];

  let pipelines: Array<{ id: string; name: string }> = [];
  if (uniquePipelineIds.length) {
    const { data, error } = await supabase
      .from('pipelines')
      .select('id, name')
      .in('id', uniquePipelineIds);
    throwIfError(error);
    pipelines = data || [];
  }

  const linkedById = new Map(linkedCards.map(card => [card.id, card]));
  const pipelineById = new Map(pipelines.map(pipeline => [pipeline.id, pipeline]));

  return rows.map(row => {
    if (!row.card) return row;
    const linkedCard = row.card.linked_card_id ? linkedById.get(row.card.linked_card_id) : null;
    return {
      ...row,
      card: {
        ...row.card,
        pipeline: pipelineById.get(row.card.pipeline_id) || null,
        linked_card: linkedCard ? {
          ...linkedCard,
          pipeline: pipelineById.get(linkedCard.pipeline_id) || null,
        } : null,
      },
    };
  });
};

// Rótulos pt-br dos status técnicos de reunião (meetings.status).
const MEETING_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Agendada',
  confirmed: 'Confirmada',
  done: 'Realizada',
  no_show: 'Não compareceu',
  cancelled: 'Cancelada',
};

interface NotifyMeetingStatusOpts {
  meetingId: string | null;
  leadId: string | null;
  statusCode: DbMeetingStatus;
  extra?: Record<string, unknown>;
}

// Dispara o webhook MKT de reunião (fire-and-forget, non-fatal).
const notifyMeetingStatus = (notify: (action: MktAction, opts: NotifyMktConversionOpts) => void, action: MktAction, { meetingId, leadId, statusCode, extra = {} }: NotifyMeetingStatusOpts): void => {
  if (!meetingId || !leadId) return;
  (async () => {
    try {
      const supabase = getSupabase();
      let m: {
        id: string;
        scheduled_at: string | null;
        duration_minutes: number;
        link: string | null;
        closer_id: string | null;
        sdr_id: string | null;
      } | null = null;
      if (supabase) {
        const { data } = await supabase
          .from('meetings')
          .select('id, scheduled_at, duration_minutes, link, closer_id, sdr_id')
          .eq('id', meetingId)
          .maybeSingle();
        m = data || null;
      }
      notify(action, {
        leadId,
        meeting: {
          id: meetingId,
          status: statusCode,
          status_label: MEETING_STATUS_LABELS[statusCode] || statusCode,
          scheduled_at: m?.scheduled_at || null,
          duration_min: m?.duration_minutes ?? null,
          link: m?.link || null,
          closer_id: m?.closer_id || null,
          sdr_id: m?.sdr_id || null,
          ...extra,
        },
      });
    } catch (err) {
      console.warn(`[SupabaseAgendaRepository] notifyMeetingStatus(${action}) falhou:`, (err as Error)?.message || err);
    }
  })();
};

interface EnsureCloserCardParams {
  meetingId: string;
  leadId: string;
  sdrCardId: string;
  closerId: string;
  closerPipeId: string;
}

const isEnterpriseWorkspaceId = async (supabase: TypedSupabaseClient, workspaceId?: string | null): Promise<boolean> => {
  if (!workspaceId) return false;
  const { data } = await supabase
    .from('workspaces')
    .select('slug')
    .eq('id', workspaceId)
    .maybeSingle();
  return ['enterprise', 'enterprise-matriz', 'aex'].includes(String(data?.slug || '').toLowerCase());
};

// O workspace nem sempre chega no params (o modal de agenda pode omitir).
// Como o webhook é escopado por workspace, resolvemos pelo próprio lead.
const resolveLeadWorkspaceId = async (supabase: TypedSupabaseClient, leadId?: string | null): Promise<string | null> => {
  if (!leadId) return null;
  const { data } = await supabase
    .from('leads')
    .select('workspace_id')
    .eq('id', leadId)
    .maybeSingle();
  return data?.workspace_id || null;
};

// Verifica se a meeting tem um closer card ativo. Se não, cria ou reativa.
async function ensureCloserCard(supabase: TypedSupabaseClient, { meetingId, leadId, sdrCardId, closerId, closerPipeId }: EnsureCloserCardParams): Promise<void> {
  if (!meetingId || !closerPipeId) return;

  const { data: meeting } = await supabase
    .from('meetings')
    .select('card_id')
    .eq('id', meetingId)
    .single();

  if (meeting?.card_id) {
    const { data: card } = await supabase
      .from('cards')
      .select('id, status, ativo')
      .eq('id', meeting.card_id)
      .single();
    if (card && (card.status === 'lost' || card.ativo === false)) {
      await supabase
        .from('cards')
        .update({ status: 'active', ativo: true, lost_at: null })
        .eq('id', card.id);
    }
    return;
  }

  const { data: stages } = await supabase
    .from('pipeline_stages')
    .select('id')
    .eq('pipeline_id', closerPipeId)
    .eq('ativo', true)
    .order('order_index', { ascending: true })
    .limit(1);
  const firstStageId = stages?.[0]?.id;
  if (!firstStageId) return;

  const { data: existing } = await supabase
    .from('cards')
    .select('id')
    .eq('lead_id', leadId)
    .eq('pipeline_id', closerPipeId)
    .order('created_at', { ascending: false })
    .limit(1);

  let cardId: string | undefined;
  if (existing?.[0]?.id) {
    cardId = existing[0].id;
    await supabase
      .from('cards')
      .update({
        status: 'active', ativo: true, lost_at: null,
        stage_id: firstStageId, closer_id: closerId,
        linked_card_id: sdrCardId,
      })
      .eq('id', cardId);
  } else {
    const insertCard: CardsInsert = {
      lead_id: leadId, pipeline_id: closerPipeId,
      stage_id: firstStageId, closer_id: closerId,
      sdr_id: null, linked_card_id: sdrCardId,
      status: 'active', ativo: true, order_index: 0,
    };
    const { data: newCard } = await supabase
      .from('cards')
      .insert(insertCard)
      .select('id')
      .single();
    cardId = newCard?.id;
  }

  if (cardId) {
    await supabase
      .from('meetings')
      .update({ card_id: cardId })
      .eq('id', meetingId);
  }
}

// ── Helpers de history (insert direto) ──────────────────────────────
interface InsertHistoryParams {
  workspaceId: string | null;
  leadId: string | null;
  cardId: string | null;
  type: string;
  title: string;
  description?: string | null;
  data?: Json | null;
  userId?: string | null;
}

const insertHistory = async ({ workspaceId, leadId, cardId, type, title, description = null, data = null, userId = null }: InsertHistoryParams) => {
  const supabase = requireSupabase();
  let createdBy = userId || null;
  if (!createdBy) {
    const { data: { user } } = await supabase.auth.getUser();
    createdBy = user?.id || null;
  }
  return supabase.from('history').insert({
    workspace_id: workspaceId,
    lead_id:      leadId,
    card_id:      cardId,
    type,
    title,
    description,
    data,
    created_by:   createdBy,
  } as HistoryInsert);
};

// ══════════════════════════════════════════════════════════════════
//  Factory do adapter
// ══════════════════════════════════════════════════════════════════
export class SupabaseAgendaRepository implements IAgendaRepository {
  // notifyMktConversion é do domínio mkt; injetado no core.ts (evita import
  // de @app aqui, que causaria ciclo com o composition root). notifyWebhook
  // segue o mesmo padrão para o webhook de saída do workspace (Config/87).
  constructor(
    private readonly notifyMkt: (action: MktAction, opts: NotifyMktConversionOpts) => void,
    private readonly notifyWebhook: (params: DispatchWebhookParams) => void = () => {},
  ) {}

    getMeetingsByWeek = async ({ workspaceId, startISO, endISO, closerId = null }: GetMeetingsByWeekParams): Promise<AgendaMeeting[]> => {
      if (!workspaceId || !startISO || !endISO) return [];
      const supabase = requireSupabase();

      let q = supabase
        .from('meetings')
        .select(SELECT_RICH)
        .eq('workspace_id', workspaceId)
        .gte('scheduled_at', startISO)
        .lt('scheduled_at', endISO)
        .order('scheduled_at', { ascending: true });

      if (closerId) q = q.eq('closer_id', closerId);

      const { data, error } = await q;
      throwIfError(error);
      return enrichMeetingCards(supabase, (data || []) as unknown as AgendaMeeting[]);
    };

    getAvailableSlots = async ({ closerId, date, durationMin = 60 }: GetAvailableSlotsParams): Promise<AvailableSlot[]> => {
      if (!closerId || !date) return [];
      const supabase = requireSupabase();
      const { data, error } = await supabase.rpc('get_available_slots', {
        p_closer_id:    closerId,
        p_date:         date,
        p_duration_min: durationMin,
      });
      throwIfError(error);
      return data || [];
    };

    scheduleMeeting = async ({
      workspaceId = null,
      leadId, sdrCardId, closerId, closerPipeId,
      scheduledAt, durationMin = 60, link = null, summary = null,
    }: ScheduleMeetingParams): Promise<string> => {
      const supabase = requireSupabase();
      const enterpriseMode = await isEnterpriseWorkspaceId(supabase, workspaceId);
      const { data, error } = await supabase.rpc('schedule_meeting', {
        p_lead_id:        leadId,
        p_sdr_card_id:    sdrCardId,
        p_closer_id:      closerId,
        p_closer_pipe_id: closerPipeId,
        p_scheduled_at:   scheduledAt,
        p_duration_min:   durationMin,
        p_link:           link ?? undefined,
        p_summary:        summary ?? undefined,
      });
      throwIfError(error);
      const meetingId = data as string;

      // Garante o card no pipe de Apresentação (closer) — inclusive Enterprise/
      // Matriz, que agora também recebem o card ao agendar (Config/73).
      try {
        await ensureCloserCard(supabase, { meetingId, leadId, sdrCardId, closerId, closerPipeId });
      } catch (err) {
        console.warn('[scheduleMeeting] ensureCloserCard falhou:', (err as Error)?.message);
      }

      // Lead agendado vira "Qualificado". Não rebaixa won/lost.
      if (!enterpriseMode) {
        try {
          const { data: qualified, error: stErr } = await supabase
            .from('leads')
            .update({ status: 'qualified' })
            .eq('id', leadId)
            .in('status', ['new', 'contacted', 'proposal'])
            .select('id');
          if (stErr) {
            console.warn('[scheduleMeeting] Falha ao qualificar lead:', stErr.message);
          } else if (qualified && qualified.length) {
            this.notifyMkt('qualificado', { leadId });
          }
        } catch (err) {
          console.warn('[scheduleMeeting] Falha ao qualificar lead:', (err as Error)?.message);
        }
      }

      notifyMeetingStatus(this.notifyMkt, 'reuniao-agendada', { meetingId, leadId, statusCode: 'scheduled' });

      // Webhook do workspace — agendamento. Dedup pelo meeting_id: reagendar
      // gera uma reunião nova, mas o mesmo agendamento nunca sai duas vezes.
      try {
        const wsId = workspaceId || (await resolveLeadWorkspaceId(supabase, leadId));
        if (wsId) {
          this.notifyWebhook({
            event: 'meeting.scheduled',
            workspaceId: wsId,
            leadId,
            dedupeKey: meetingId,
            data: { meeting_id: meetingId },
          });
        }
      } catch (err) {
        console.warn('[scheduleMeeting] webhook meeting.scheduled falhou:', (err as Error)?.message);
      }

      return meetingId;
    };

    updateMeetingLink = async ({ meetingId, link }: UpdateMeetingLinkParams): Promise<boolean> => {
      if (!meetingId || !link) return false;
      const supabase = requireSupabase();
      const { error } = await supabase
        .from('meetings')
        .update({ link })
        .eq('id', meetingId);
      throwIfError(error);
      return true;
    };

    editMeetingSchedule = async ({ meetingId, scheduledAt, durationMin = 60 }: EditMeetingScheduleParams): Promise<boolean> => {
      if (!meetingId || !scheduledAt) throw new Error('meetingId e scheduledAt obrigatórios.');
      const supabase = requireSupabase();

      const { data: rawMeetData } = await supabase
        .from('meetings')
        .select('link, closer_id, closer:profiles!meetings_closer_id_fkey ( nome )')
        .eq('id', meetingId)
        .single();
      const meetData = rawMeetData as unknown as MeetCloserLite | null;

      const meetLink = meetData?.link || null;
      const closerNome = meetData?.closer?.nome || null;
      const closerIdVal = meetData?.closer_id || null;

      const startDate = new Date(scheduledAt);
      const endDate = new Date(startDate.getTime() + durationMin * 60000);

      const { error } = await supabase
        .from('meetings')
        .update({ scheduled_at: startDate.toISOString(), duration_minutes: durationMin })
        .eq('id', meetingId);
      throwIfError(error);

      if (meetLink) {
        const n8nUser = process.env.REACT_APP_N8N_MEET_WEBHOOK_USER;
        const n8nPass = process.env.REACT_APP_N8N_MEET_WEBHOOK_PASS;
        const auth = n8nUser && n8nPass && typeof btoa === 'function'
          ? btoa(`${n8nUser}:${n8nPass}`)
          : null;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (auth) headers.Authorization = `Basic ${auth}`;

        fetch('https://n8n.2.empreenderdinheiro.com.br/webhook/crm/editar-reunião', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            closer: closerNome,
            closer_id: closerIdVal,
            link: meetLink,
            start: startDate.toISOString(),
            end: endDate.toISOString(),
          }),
        }).catch(err => console.warn('[editMeetingSchedule] Webhook editar falhou:', (err as Error)?.message));
      }

      return true;
    };

    swapMeetingCloser = async ({
      meetingId, newCloserId, closerCardId = null, sdrCardId = null,
      leadId, workspaceId, scheduledAt = null, durationMin = null,
      leadName = null, funnelName = null, userId = null,
    }: SwapMeetingCloserParams): Promise<false | SwapMeetingCloserResult> => {
      if (!meetingId || !newCloserId) throw new Error('meetingId e newCloserId obrigatórios.');
      const supabase = requireSupabase();

      const { data: rawMeet } = await supabase
        .from('meetings')
        .select('link, closer_id, scheduled_at, duration_minutes, card_id, closer:profiles!meetings_closer_id_fkey ( nome )')
        .eq('id', meetingId)
        .single();
      const meet = rawMeet as unknown as MeetCloserLite | null;
      const oldLink        = meet?.link || null;
      const oldCloserNome  = meet?.closer?.nome || null;
      const oldCloserId    = meet?.closer_id || null;
      const finalSchedAt   = scheduledAt || meet?.scheduled_at || null;
      const finalDuration  = Number(durationMin || meet?.duration_minutes || 60);
      const resolvedCardId = closerCardId || meet?.card_id || null;

      if (oldCloserId === newCloserId && !scheduledAt) return false;

      postCancelMeetingWebhook({ closerNome: oldCloserNome, closerId: oldCloserId, link: oldLink });

      const upd: MeetingsUpdate = { closer_id: newCloserId, status: 'scheduled', link: null };
      if (finalSchedAt)  upd.scheduled_at    = finalSchedAt;
      if (finalDuration) upd.duration_minutes = finalDuration;
      const { error: e1 } = await supabase.from('meetings').update(upd).eq('id', meetingId);
      throwIfError(e1);

      if (resolvedCardId) {
        const { error: e2 } = await supabase
          .from('cards').update({ closer_id: newCloserId }).eq('id', resolvedCardId);
        if (e2) console.warn('[swapMeetingCloser] falha ao reatribuir card closer:', e2.message);
      }

      if (sdrCardId) {
        const { error: e3 } = await supabase
          .from('cards').update({ confirmed: false }).eq('id', sdrCardId);
        if (e3) console.warn('[swapMeetingCloser] falha ao limpar confirmação do card SDR:', e3.message);
      }

      let newLink: string | null = null;
      if (finalSchedAt) {
        newLink = await generateMeetLink({
          meetingId, closerId: newCloserId, scheduledAt: finalSchedAt, leadName, funnelName,
        });
        if (newLink) {
          await supabase.from('meetings').update({ link: newLink }).eq('id', meetingId);
        }
      }

      await insertHistory({
        workspaceId: workspaceId ?? null, leadId: leadId ?? null, cardId: sdrCardId || resolvedCardId,
        type: 'status_alterado',
        title: 'Closer da reunião trocado',
        description: oldCloserNome ? `Closer anterior: ${oldCloserNome}` : null,
        data: { meeting_id: meetingId, old_closer_id: oldCloserId, new_closer_id: newCloserId },
        userId,
      }).catch(() => { /* non-fatal */ });

      notifyMeetingStatus(this.notifyMkt, 'reuniao-agendada', {
        meetingId, leadId: leadId ?? null, statusCode: 'scheduled',
        extra: { swapped_closer: true, old_closer_id: oldCloserId, new_closer_id: newCloserId },
      });

      return { meetingId, newCloserId, link: newLink, scheduledAt: finalSchedAt };
    };

    clearCardReagend = async (cardId: string): Promise<boolean> => {
      if (!cardId) return false;
      const supabase = requireSupabase();
      const { error } = await supabase
        .from('cards')
        .update({ reagend: false })
        .eq('id', cardId);
      throwIfError(error);
      return true;
    };

    listMeetingsForOrdinals = async (leadIds: string[]): Promise<MeetingOrdinalRow[]> => {
      if (!leadIds.length) return [];
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('meetings')
        .select('id, lead_id, scheduled_at, created_at')
        .in('lead_id', leadIds)
        .order('created_at', { ascending: true })
        .order('scheduled_at', { ascending: true, nullsFirst: false });
      throwIfError(error);
      return (data || []) as MeetingOrdinalRow[];
    };

    discardReschedule = async ({
      mode = 'archive', sdrCardId = null, closerCardId = null,
      meetingId = null, leadId = null, workspaceId = null,
      reason = null, userId = null,
    }: DiscardRescheduleParams): Promise<boolean> => {
      if (!sdrCardId && !closerCardId) {
        throw new Error('Nenhum card associado para remover.');
      }
      const supabase = requireSupabase();

      if (meetingId) {
        const { error } = await supabase
          .from('meetings')
          .update({ status: 'cancelled', result: reason || 'Removido da agenda' })
          .eq('id', meetingId)
          .in('status', ['scheduled', 'confirmed']);
        if (error) console.warn('[discardReschedule] Falha ao cancelar reunião:', error.message);
      }

      if (mode === 'lost') {
        const primary = sdrCardId || closerCardId;
        const { data, error } = await supabase.rpc('mark_card_lost', {
          p_card_id:     primary as string,
          p_loss_reason: reason || 'Não quis agendar',
        });
        throwIfError(error);
        const result = data as { success?: boolean; error?: string; linked_card_id?: string } | null;
        if (result?.success === false) throw new Error(result.error || 'Erro ao marcar como perdido.');
        const other = primary === sdrCardId ? closerCardId : sdrCardId;
        if (other && result?.linked_card_id !== other) {
          await Promise.resolve(supabase.rpc('mark_card_lost', {
            p_card_id: other, p_loss_reason: reason || 'Não quis agendar',
          })).catch(() => { /* idempotente; non-fatal */ });
        }

        if (leadId) {
          this.notifyMkt('perda', {
            leadId,
            loss: {
              motivo: reason || 'Não quis agendar',
              cards: [sdrCardId, closerCardId].filter(Boolean).map(id => ({ card_id: id })),
              marcado_por: userId ? { id: userId } : null,
            },
          });
        }
      } else {
        const ids = [sdrCardId, closerCardId].filter(Boolean) as string[];
        const { error } = await supabase
          .from('cards')
          .update({ ativo: false, reagend: false })
          .in('id', ids);
        throwIfError(error);
        if (workspaceId && leadId) {
          await insertHistory({
            workspaceId, leadId, cardId: sdrCardId || closerCardId,
            type: 'arquivado', title: 'Lead arquivado da agenda',
            description: reason || 'Lead não quis agendar',
            userId,
          }).catch(() => { /* non-fatal */ });
        }
      }

      return true;
    };

    handoffLeadToAgenda = async ({
      leadId, sdrCardId, closerId, closerPipeId, link = null, summary = null,
    }: HandoffLeadToAgendaParams): Promise<Json> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase.rpc('ensure_agenda_handoff', {
        p_lead_id:        leadId,
        p_sdr_card_id:    sdrCardId,
        p_closer_id:      closerId,
        p_closer_pipe_id: closerPipeId,
        p_link:           link ?? undefined,
        p_summary:        summary ?? undefined,
      });
      throwIfError(error);
      return data;
    };

    getPendingAgendaMeetings = async ({ workspaceId, closerId = null, sdrId = null }: PendingAgendaMeetingsParams): Promise<AgendaMeeting[]> => {
      if (!workspaceId) return [];
      const supabase = requireSupabase();

      let q = supabase
        .from('meetings')
        .select(SELECT_RICH)
        .eq('workspace_id', workspaceId)
        .is('scheduled_at', null)
        .in('status', ['scheduled', 'confirmed'])
        .order('created_at', { ascending: false });

      if (closerId) q = q.eq('closer_id', closerId);
      if (sdrId)    q = q.eq('sdr_id', sdrId);

      const { data, error } = await q;
      throwIfError(error);
      return enrichMeetingCards(supabase, (data || []) as unknown as AgendaMeeting[]);
    };

    getPendingReschedules = async ({ workspaceId, closerId = null, sdrId = null }: PendingReschedulesParams): Promise<PendingRescheduleRow[]> => {
      if (!workspaceId) return [];
      const supabase = requireSupabase();

      let cardsQuery = supabase
        .from('cards')
        .select(`
          *,
          lead:leads(id, workspace_id, nome, telefone, patrimonio, status),
          closer:profiles!cards_closer_id_fkey(id, nome, initials),
          sdr:profiles!cards_sdr_id_fkey(id, nome, initials),
          pipeline:pipelines(id, name, role, closer_pipeline_id)
        `)
        .eq('reagend', true)
        .eq('status', 'active')
        .eq('ativo', true)
        .order('updated_at', { ascending: true });

      if (closerId) cardsQuery = cardsQuery.eq('closer_id', closerId);
      if (sdrId)    cardsQuery = cardsQuery.eq('sdr_id', sdrId);

      let cancelledQuery = supabase
        .from('meetings')
        .select(SELECT_RICH)
        .eq('workspace_id', workspaceId)
        .in('status', ['cancelled', 'no_show'])
        .order('updated_at', { ascending: false });

      if (closerId) cancelledQuery = cancelledQuery.eq('closer_id', closerId);
      if (sdrId)    cancelledQuery = cancelledQuery.eq('sdr_id', sdrId);

      let activeQuery = supabase
        .from('meetings')
        .select(SELECT_RICH)
        .eq('workspace_id', workspaceId)
        .in('status', ['scheduled', 'confirmed', 'done']);

      if (closerId) activeQuery = activeQuery.eq('closer_id', closerId);
      if (sdrId)    activeQuery = activeQuery.eq('sdr_id', sdrId);

      const [cardsResult, cancelledResult, activeResult] = await Promise.all([
        cardsQuery,
        cancelledQuery,
        activeQuery,
      ]);
      throwIfError(cardsResult.error);
      throwIfError(cancelledResult.error);
      throwIfError(activeResult.error);

      let rows = ((cardsResult.data || []) as unknown as PendingRescheduleRow[]).filter(row =>
        row.lead?.workspace_id === workspaceId &&
        row.pipeline?.role === 'sdr'
      );

      const [enrichedMeetings, activeMeetings] = await Promise.all([
        enrichMeetingCards(supabase, (cancelledResult.data || []) as unknown as AgendaMeeting[]),
        enrichMeetingCards(supabase, (activeResult.data || []) as unknown as AgendaMeeting[]),
      ]);

      const alreadyHandledCardIds = new Set(
        activeMeetings.map(meeting => meeting.card?.linked_card_id).filter((v): v is string => Boolean(v))
      );

      rows = rows.filter(row => !alreadyHandledCardIds.has(row.id));

      const existingCardIds = new Set(rows.map(row => row.id).filter(Boolean));
      const latestReturnedByKey = new Map<string, PendingRescheduleRow>();
      enrichedMeetings.forEach(meeting => {
        const sdrCardId    = meeting.card?.linked_card_id || null;
        const closerCardId = meeting.card?.id || null;
        const refCardId    = sdrCardId || closerCardId;
        if (!refCardId) return;

        const refArchived = sdrCardId
          ? meeting.card?.linked_card?.ativo === false
          : meeting.card?.ativo === false;
        if (refArchived) return;

        if (existingCardIds.has(refCardId)) return;
        if (sdrCardId && alreadyHandledCardIds.has(sdrCardId)) return;
        if (latestReturnedByKey.has(refCardId)) return;

        latestReturnedByKey.set(refCardId, {
          id:        refCardId,
          lead_id:   meeting.lead_id,
          lead:      meeting.lead,
          closer_id: meeting.closer_id,
          closer:    meeting.closer,
          sdr_id:    meeting.sdr_id,
          sdr:       meeting.sdr,
          status:    'active',
          reagend:   true,
          updated_at: meeting.updated_at || meeting.created_at,
          source_meeting:   meeting,
          no_sdr_handoff: !sdrCardId,
          return_history: {
            type:       meeting.status === 'no_show' ? 'noshow' : 'cancelado',
            title:      meeting.status === 'no_show' ? 'Lead não compareceu à reunião' : 'Reunião cancelada',
            created_at: meeting.updated_at || meeting.created_at,
          },
        });
      });
      const returnedMeetings = [...latestReturnedByKey.values()];

      const combinedRows = [...rows, ...returnedMeetings];
      const cardIds = combinedRows.map(row => row.id).filter(Boolean);
      if (!cardIds.length) return combinedRows;

      const { data: historyRows, error: historyError } = await supabase
        .from('history')
        .select('id, card_id, type, title, description, created_at')
        .in('card_id', cardIds)
        .in('type', ['noshow', 'cancelado'])
        .order('created_at', { ascending: false });
      throwIfError(historyError);

      const lastByCard = new Map<string, { id: string; card_id: string | null; type: string; title: string; description: string | null; created_at: string }>();
      (historyRows || []).forEach(item => {
        if (item.card_id && !lastByCard.has(item.card_id)) lastByCard.set(item.card_id, item);
      });

      return combinedRows.map(row => ({
        ...row,
        return_history: lastByCard.get(row.id) || row.return_history || null,
      }));
    };

    confirmPresence = async ({ meetingId, sdrCardId, leadId, workspaceId, userId = null }: ConfirmPresenceParams): Promise<boolean> => {
      if (!meetingId) throw new Error('meetingId obrigatório.');
      const supabase = requireSupabase();

      const { error: meetingError } = await supabase
        .from('meetings').update({ status: 'confirmed' }).eq('id', meetingId);
      if (meetingError) throw meetingError;

      if (sdrCardId) {
        const { error: cardError } = await supabase
          .from('cards').update({ confirmed: true }).eq('id', sdrCardId);
        if (cardError) {
          console.warn('[SupabaseAgendaRepository] Reunião confirmada, mas o card não pôde ser marcado como confirmado:', cardError);
        }
      }

      if (workspaceId && leadId) {
        await insertHistory({
          workspaceId, leadId, cardId: sdrCardId || null,
          type: 'confirmado', title: 'Presença confirmada (D-1)',
          data: { meeting_id: meetingId },
          userId,
        });
      }

      notifyMeetingStatus(this.notifyMkt, 'reuniao-confirmada', { meetingId, leadId: leadId ?? null, statusCode: 'confirmed' });

      return true;
    };

    cancelMeeting = async ({ meetingId, sdrCardId, closerCardId, leadId, workspaceId, reason = null, userId = null }: CancelMeetingParams): Promise<boolean> => {
      if (!meetingId) throw new Error('meetingId obrigatório.');
      const supabase = requireSupabase();

      let meetLink: string | null = null;
      let closerNome: string | null = null;
      let closerId: string | null = null;
      try {
        const { data: rawMeetData } = await supabase
          .from('meetings')
          .select('link, closer_id, closer:profiles!meetings_closer_id_fkey ( nome )')
          .eq('id', meetingId)
          .single();
        const meetData = rawMeetData as unknown as MeetCloserLite | null;
        meetLink = meetData?.link || null;
        closerNome = meetData?.closer?.nome || null;
        closerId = meetData?.closer_id || null;
      } catch { /* non-fatal */ }

      const { error: e3 } = await supabase
        .from('meetings').update({ status: 'cancelled', result: reason || null }).eq('id', meetingId);
      if (e3) throw e3;

      if (sdrCardId) {
        try {
          const { data: card } = await supabase
            .from('cards').select('pipeline_id').eq('id', sdrCardId).single();
          let emContatoStageId: string | null = null;
          if (card?.pipeline_id) {
            emContatoStageId = await findEmContatoStageId(supabase, card.pipeline_id);
          }

          const updateSdr: CardsUpdate = {
            cancelled_appt: true,
            confirmed: false,
            reagend: true,
          };
          if (emContatoStageId) updateSdr.stage_id = emContatoStageId;
          const { error: e1 } = await supabase
            .from('cards').update(updateSdr).eq('id', sdrCardId);
          if (e1) console.warn('[cancelMeeting] Falha ao atualizar card SDR:', e1);
        } catch (err) {
          console.warn('[cancelMeeting] Card SDR não encontrado ou inacessível:', (err as Error)?.message);
        }
      }

      if (closerCardId) {
        const { error: e2 } = await supabase
          .from('cards').update({ status: 'lost', lost_at: new Date().toISOString() }).eq('id', closerCardId);
        if (e2) console.warn('[cancelMeeting] Falha ao atualizar card Closer:', e2);
      }

      await insertHistory({
        workspaceId: workspaceId ?? null, leadId: leadId ?? null, cardId: sdrCardId ?? null,
        type: 'cancelado', title: 'Reunião cancelada',
        description: reason,
        data: { meeting_id: meetingId },
        userId,
      });

      if (meetLink) {
        const n8nUser = process.env.REACT_APP_N8N_MEET_WEBHOOK_USER;
        const n8nPass = process.env.REACT_APP_N8N_MEET_WEBHOOK_PASS;
        const auth = n8nUser && n8nPass && typeof btoa === 'function'
          ? btoa(`${n8nUser}:${n8nPass}`)
          : null;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (auth) headers.Authorization = `Basic ${auth}`;

        fetch('https://n8n.2.empreenderdinheiro.com.br/webhook/crm/cancelar-reunião', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            closer: closerNome,
            closer_id: closerId,
            link: meetLink,
          }),
        }).catch(err => console.warn('[cancelMeeting] Webhook cancelar falhou:', (err as Error)?.message));
      }

      notifyMeetingStatus(this.notifyMkt, 'reuniao-cancelada', {
        meetingId, leadId: leadId ?? null, statusCode: 'cancelled',
        extra: { reason: reason || null, closer: closerNome || null },
      });

      return true;
    };

    registerNoShow = async ({ meetingId, sdrCardId, leadId, workspaceId, observation = null, userId = null }: RegisterNoShowParams): Promise<boolean> => {
      if (!meetingId) throw new Error('meetingId obrigatório.');
      const supabase = requireSupabase();

      if (sdrCardId) {
        const { data: card } = await supabase
          .from('cards').select('pipeline_id').eq('id', sdrCardId).single();
        let emContatoStageId: string | null = null;
        if (card?.pipeline_id) {
          emContatoStageId = await findEmContatoStageId(supabase, card.pipeline_id);
        }
        const updateSdr: CardsUpdate = { reagend: true };
        if (emContatoStageId) updateSdr.stage_id = emContatoStageId;
        const { error: e1 } = await supabase
          .from('cards').update(updateSdr).eq('id', sdrCardId);
        if (e1) throw e1;
      }

      const { data: updated, error: e2 } = await supabase
        .from('meetings')
        .update({ status: 'no_show', result: observation || null })
        .eq('id', meetingId)
        .select('id');

      if (e2) throw e2;

      if (!updated || updated.length === 0) {
        const { error: retryErr } = await supabase.rpc('update_meeting_status' as never, {
          p_meeting_id: meetingId,
          p_status: 'no_show',
          p_result: observation || null,
        } as never);
        if (retryErr) {
          console.warn('[registerNoShow] RPC fallback falhou:', retryErr.message);
          await supabase
            .from('meetings')
            .update({ status: 'no_show', result: observation || null })
            .eq('id', meetingId);
        }
      }

      await insertHistory({
        workspaceId: workspaceId ?? null, leadId: leadId ?? null, cardId: sdrCardId ?? null,
        type: 'noshow', title: 'Lead não compareceu à reunião',
        description: observation,
        data: { meeting_id: meetingId },
        userId,
      });

      notifyMeetingStatus(this.notifyMkt, 'reuniao-noshow', {
        meetingId, leadId: leadId ?? null, statusCode: 'no_show',
        extra: { observation: observation || null },
      });

      return true;
    };

    registerResult = async ({ meetingId, sdrCardId, leadId, workspaceId, result, userId = null }: RegisterResultParams): Promise<boolean> => {
      if (!meetingId) throw new Error('meetingId obrigatório.');
      if (!result?.trim()) throw new Error('Resultado obrigatório.');
      const supabase = requireSupabase();

      const { data: updated, error: e1 } = await supabase
        .from('meetings')
        .update({ status: 'done', result: result.trim() })
        .eq('id', meetingId)
        .select('id');

      if (e1) throw e1;

      if (!updated || updated.length === 0) {
        const { error: retryErr } = await supabase.rpc('update_meeting_status' as never, {
          p_meeting_id: meetingId,
          p_status: 'done',
          p_result: result.trim(),
        } as never);

        if (retryErr) {
          console.warn('[registerResult] RPC fallback falhou, tentando update direto:', retryErr.message);
          const { error: rawErr } = await supabase
            .from('meetings')
            .update({ status: 'done', result: result.trim() })
            .eq('id', meetingId);
          if (rawErr) throw rawErr;
        }
      }

      if (sdrCardId) {
        const { error: e2 } = await supabase
          .from('cards').update({ status: 'concluido' }).eq('id', sdrCardId);
        if (e2) console.warn('[registerResult] Falha ao atualizar card SDR:', e2);

        try {
          const { data: sdrCard } = await supabase
            .from('cards').select('pipeline_id').eq('id', sdrCardId).maybeSingle();
          const stageId = await findFinalizadoStageId(supabase, sdrCard?.pipeline_id ?? null);
          if (stageId) {
            const { error: e3 } = await supabase
              .from('cards').update({ stage_id: stageId }).eq('id', sdrCardId);
            if (e3) console.warn('[registerResult] Falha ao mover card SDR para Finalizado:', e3);
          }
        } catch (mvErr) {
          console.warn('[registerResult] Erro ao mover card SDR para Finalizado:', mvErr);
        }
      }

      await insertHistory({
        workspaceId: workspaceId ?? null, leadId: leadId ?? null, cardId: sdrCardId ?? null,
        type: 'realizada', title: 'Reunião realizada',
        description: result.trim(),
        data: { meeting_id: meetingId },
        userId,
      });

      notifyMeetingStatus(this.notifyMkt, 'reuniao-realizada', {
        meetingId, leadId: leadId ?? null, statusCode: 'done',
        extra: { result: result.trim() },
      });

      return true;
    };

    updateMeetingStatusDirect = async ({ meetingId, newStatus, workspaceId, leadId, cardId, userId = null }: UpdateMeetingStatusDirectParams): Promise<boolean> => {
      if (!meetingId || !newStatus) throw new Error('meetingId e newStatus obrigatórios.');
      const supabase = requireSupabase();
      const { error } = await supabase
        .from('meetings')
        .update({ status: newStatus as DbMeetingStatus })
        .eq('id', meetingId);
      throwIfError(error);
      if (workspaceId && leadId) {
        await insertHistory({
          workspaceId, leadId, cardId: cardId || null,
          type: 'status_alterado',
          title: 'Status alterado no card da agenda.',
          data: { meeting_id: meetingId, status: newStatus },
          userId,
        });
      }
      return true;
    };

    // ── Disponibilidade — Agenda do Google (todos os workspaces) ─
    // Dupla validação: o time comercial atua em vários workspaces, então a
    // ocupação real do closer só é vista consultando a agenda do Google (que
    // não está sujeita ao RLS por workspace do CRM). Retorna os intervalos
    // ocupados no range; se vier vazio, o closer está livre.
    getCloserGoogleBusy = async (closerId: string, startISO: string, endISO: string): Promise<Array<{ start: string; end: string }>> => {
      if (!closerId || !startISO || !endISO) return [];
      const base = 'https://n8n.2.empreenderdinheiro.com.br/webhook/7dc1ecdb-ab01-4415-8a1b-abe54129ebca/crm/disponibilidade';
      const url = `${base}/${encodeURIComponent(closerId)}?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`;

      const n8nUser = process.env.REACT_APP_N8N_MEET_WEBHOOK_USER;
      const n8nPass = process.env.REACT_APP_N8N_MEET_WEBHOOK_PASS;
      const auth = n8nUser && n8nPass && typeof btoa === 'function'
        ? btoa(`${n8nUser}:${n8nPass}`)
        : null;
      const headers: Record<string, string> = {};
      if (auth) headers.Authorization = `Basic ${auth}`;

      try {
        const res = await fetch(url, { method: 'GET', headers });
        if (!res.ok) return [];
        const body = await res.json().catch(() => null);
        return parseGoogleBusyIntervals(body);
      } catch (err) {
        console.warn('[SupabaseAgendaRepository] getCloserGoogleBusy falhou:', (err as Error)?.message);
        return [];
      }
    };

    // ── Disponibilidade — leitura ───────────────────────────────
    getCloserBusyMeetings = async (closerId: string, startISO: string, endISO: string): Promise<Array<{ scheduled_at: string | null; duration_minutes: number | null; status: string | null }>> => {
      const supabase = requireSupabase();
      const { data } = await supabase
        .from('meetings')
        .select('scheduled_at, duration_minutes, status')
        .eq('closer_id', closerId)
        .neq('status', 'cancelled')
        .gte('scheduled_at', startISO)
        .lt('scheduled_at', endISO);
      return (data || []) as Array<{ scheduled_at: string | null; duration_minutes: number | null; status: string | null }>;
    };

    resetMeetingToScheduled = async (meetingId: string, sdrCardId: string | null): Promise<void> => {
      const supabase = requireSupabase();
      const { error } = await supabase
        .from('meetings')
        .update({ status: 'scheduled', result: null })
        .eq('id', meetingId);
      throwIfError(error);
      if (sdrCardId) {
        await supabase.from('cards').update({ confirmed: false }).eq('id', sdrCardId);
      }
    };

    getCloserSchedule = async (closerId: string): Promise<CloserScheduleRow[]> => {
      if (!closerId) return [];
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('closer_schedule')
        .select('*')
        .eq('closer_id', closerId)
        .order('day_of_week', { ascending: true });
      throwIfError(error);
      return data || [];
    };

    getBlockedDates = async (closerId: string): Promise<BlockedDateRow[]> => {
      if (!closerId) return [];
      const supabase = requireSupabase();
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('closer_blocked_dates')
        .select('*')
        .eq('closer_id', closerId)
        .gte('date_to', today)
        .order('date_from', { ascending: true });
      throwIfError(error);
      return data || [];
    };

    // ── Disponibilidade — escrita ───────────────────────────────
    upsertCloserSchedule = async (row: CloserScheduleInsert): Promise<CloserScheduleRow | null> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('closer_schedule')
        .upsert(row, { onConflict: 'closer_id,day_of_week' })
        .select()
        .single();
      throwIfError(error);
      return data;
    };

    upsertBlockedDate = async (row: BlockedDateInsert): Promise<BlockedDateRow | null> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('closer_blocked_dates')
        .insert(row)
        .select()
        .single();
      throwIfError(error);
      return data;
    };

    deleteBlockedDate = async (id: string): Promise<boolean> => {
      const supabase = requireSupabase();
      const { error } = await supabase.from('closer_blocked_dates').delete().eq('id', id);
      throwIfError(error);
      return true;
    };

    // ── Permissões ──────────────────────────────────────────────
    getSchedulePerms = async (ownerId: string): Promise<SchedulePermWithEditor[]> => {
      if (!ownerId) return [];
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('closer_schedule_perms')
        .select('*, editor:profiles!closer_schedule_perms_editor_id_fkey ( id, nome, initials, color )')
        .eq('owner_id', ownerId);
      throwIfError(error);
      return (data || []) as unknown as SchedulePermWithEditor[];
    };

    addSchedulePerm = async ({ workspaceId, ownerId, editorId }: AddSchedulePermParams): Promise<SchedulePermRow | null> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('closer_schedule_perms')
        .insert({ workspace_id: workspaceId, owner_id: ownerId, editor_id: editorId })
        .select()
        .single();
      throwIfError(error);
      return data;
    };

    removeSchedulePerm = async (id: string): Promise<boolean> => {
      const supabase = requireSupabase();
      const { error } = await supabase.from('closer_schedule_perms').delete().eq('id', id);
      throwIfError(error);
      return true;
    };

    // ── Config ──────────────────────────────────────────────────
    getConfigValues = async (workspaceId: string, keys: string[]): Promise<ConfigKV[]> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('config')
        .select('key, value')
        .eq('workspace_id', workspaceId)
        .in('key', keys);
      throwIfError(error);
      return (data || []) as ConfigKV[];
    };

    setConfig = async (workspaceId: string, key: string, value: Json): Promise<void> => {
      const supabase = requireSupabase();
      const { error } = await supabase
        .from('config')
        .upsert({ workspace_id: workspaceId, key, value, updated_at: new Date().toISOString() });
      throwIfError(error);
    };

    // ── Listagens auxiliares (linhas cruas) ─────────────────────
    listTimeMembersByRole = async (workspaceId: string, role: string): Promise<TimeMemberRow[]> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('time_members')
        .select('user_id, role, profiles:user_id ( id, nome, initials, color, avatar_url, ativo ), times!inner ( workspace_id, ativo )')
        .eq('role', role as Database['public']['Enums']['team_role'])
        .eq('ativo', true)
        .eq('times.workspace_id', workspaceId)
        .eq('times.ativo', true);
      throwIfError(error);
      return (data || []) as unknown as TimeMemberRow[];
    };

    listWorkspaceMemberRows = async (workspaceId: string): Promise<WorkspaceMemberRow[]> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('workspace_members')
        .select('user_id, role, atende_comercial, profiles:user_id ( id, nome, initials, color, avatar_url, ativo )')
        .eq('workspace_id', workspaceId)
        .eq('ativo', true);
      throwIfError(error);
      return (data || []) as unknown as WorkspaceMemberRow[];
    };

    listPipelineMembers = async (pipelineId: string, role: string): Promise<PipelineMemberRow[]> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('pipeline_members')
        .select(`
          profile_id, ativo,
          profile:profiles!pipeline_members_profile_id_fkey (
            id, nome, initials, color, avatar_url, ativo
          )
        `)
        .eq('pipeline_id', pipelineId)
        .eq('role', role)
        .eq('ativo', true);
      throwIfError(error);
      return (data || []) as unknown as PipelineMemberRow[];
    };

    listActiveCloserScheduleIds = async (workspaceId: string): Promise<string[]> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('closer_schedule')
        .select('closer_id')
        .eq('workspace_id', workspaceId)
        .eq('active', true);
      throwIfError(error);
      return (data || []).map(r => r.closer_id).filter((v): v is string => Boolean(v));
    };

    listSdrCardRowsForLead = async (leadId: string): Promise<SdrCardForLead[]> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('cards')
        .select(`
          id, pipeline_id, stage_id, status, ativo,
          pipeline:pipelines!cards_pipeline_id_fkey ( id, name, role ),
          stage:pipeline_stages!cards_stage_id_fkey ( id, name )
        `)
        .eq('lead_id', leadId)
        .eq('ativo', true);
      throwIfError(error);
      return (data || []) as unknown as SdrCardForLead[];
    };

    getWonCards = async (startISO: string, endISO: string): Promise<WonCard[]> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('cards')
        .select(`
          id, status, faturamento, updated_at, sdr_id, closer_id,
          pipeline:pipelines!cards_pipeline_id_fkey ( workspace_id )
        `)
        .eq('status', 'won')
        .gte('updated_at', startISO)
        .lt('updated_at', endISO);
      throwIfError(error);
      return (data || []) as unknown as WonCard[];
    };

    listCloserPipelines = async (workspaceId: string): Promise<CloserPipeline[]> => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from('pipelines')
        .select('id, name, role')
        .eq('workspace_id', workspaceId)
        .eq('role', 'closer')
        .eq('ativo', true)
        .order('name', { ascending: true });
      throwIfError(error);
      return (data || []) as CloserPipeline[];
    };

    findCloserPipeline = async ({ workspaceId, sdrPipelineId }: FindCloserPipelineParams): Promise<PipelineRow | null> => {
      if (!workspaceId) return null;
      const supabase = requireSupabase();

      if (sdrPipelineId) {
        const { data: sdrPipe } = await supabase
          .from('pipelines')
          .select('id, funnel_id, closer_pipeline_id')
          .eq('id', sdrPipelineId)
          .maybeSingle();

        if (sdrPipe?.closer_pipeline_id) {
          const { data } = await supabase
            .from('pipelines')
            .select('*')
            .eq('id', sdrPipe.closer_pipeline_id)
            .eq('ativo', true)
            .maybeSingle();
          if (data) return data;
        }

        if (sdrPipe?.funnel_id) {
          const { data } = await supabase
            .from('pipelines')
            .select('*')
            .eq('workspace_id', workspaceId)
            .eq('role', 'closer')
            .eq('ativo', true)
            .eq('funnel_id', sdrPipe.funnel_id)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();
          if (data) return data;
        }
      }

      const { data, error } = await supabase
        .from('pipelines')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('role', 'closer')
        .eq('ativo', true)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      throwIfError(error);
      return data || null;
    };
}
